import { PityCounter } from "@yjsgames/pity";
import { GOLD_BOX_RARITY_WEIGHTS, MODULE_BRICK_BALANCE } from "../data/balance.js";
import { RARITY_ORDER, type ModuleRarity } from "../modules/ProjectileModule.js";

/** 0~1 난수 소스 — 주입으로 테스트 결정론 확보 (Math.random 기본). */
export type RandomSource = () => number;

export interface GoldModuleBoxState {
  readonly boxOpenCount: number;
  readonly pityCounter: number;
  /**
   * 등급 천장 누적 카운터 (1개·10개 구매 공용) — @yjsgames/pity PityCounter.counter.
   * pityInterval회마다 보장 pull(추첨 등급을 pityGuaranteeRarity 이상으로 상향). field-add(기본 0).
   */
  readonly rarityPityCounter: number;
}

/** 등급 천장 설정 — interval회마다 floorRarity 이상 보장. createGoldBox가 balance에서 주입. */
export interface RarityPityConfig {
  readonly interval: number;
  readonly floorRarity: ModuleRarity;
}

/**
 * 등급 추첨 풀의 한 엔트리 — (id, rarity). 같은 모듈이 등장 가능한 여러 등급으로 **중복 등장**할 수 있다
 * (expandedModulePool이 effect{Grade} 칸 기준으로 펼침). effect 없는 등급은 풀에 없어 최소 등급이 표현된다.
 */
export interface RarityDef {
  readonly id: string;
  readonly rarity: ModuleRarity;
}

/** 상자 개봉 결과 — 지급할 (정의 id, 추첨된 등급). 인스턴스 생성 시 이 등급을 rarity 오버라이드로 사용한다. */
export interface ModuleDrop {
  readonly id: string;
  readonly rarity: ModuleRarity;
}

/**
 * 골드 모듈 상자 (지시문 §6·§16·§19) — **등급 먼저 추첨 → 그 등급 내 모듈 균등(n분의 1)** 선택해
 * (id, rarity)를 지급한다. 풀이 (id, rarity)로 펼쳐져 있어 같은 모듈이 등급별로 등장하며, 추첨된 등급이
 * 인스턴스 등급이 된다(합성 없이도 등급별 효과 차등이 체감). 순수 로직 — 결과 지급은 호출자(GameScene) 책임.
 * 보장(pity): guaranteeAt회 개봉마다 도감 미발견 정의를 그 **최소 등급**으로 우선 지급. 미발견 반환 시 pity 리셋.
 * 확률 0 등급은 풀에서 제외 — 현재 transcendent는 합성 전용("나중" 컨텐츠).
 */
export class GoldModuleBox {
  private _boxOpenCount: number;
  private _pity: number;
  private readonly pool: readonly RarityDef[];
  /**
   * 등급 천장 카운터 (선택) — 주입 시 매 open마다 advance, 보장 pull이면 등급 floor 상향.
   * 미주입(기존 테스트·레거시 경로)이면 천장 무동작 — 단발 추첨 동작 그대로 보존.
   */
  private readonly rarityPity: PityCounter | null;
  private readonly floorRarity: ModuleRarity | null;
  /** 천장 미주입 경로의 저장 왕복 보존용 (rarityPityCounter를 잃지 않게). */
  private readonly rarityPityFallback: number;

  constructor(
    defs: readonly RarityDef[],
    private readonly rng: RandomSource = Math.random,
    private readonly weights: Record<string, number> = GOLD_BOX_RARITY_WEIGHTS,
    private readonly guaranteeAt: number = MODULE_BRICK_BALANCE.guaranteedBoxOpenCount,
    initial?: GoldModuleBoxState,
    pity?: RarityPityConfig,
  ) {
    this.pool = defs.filter((d) => (this.weights[d.rarity] ?? 0) > 0);
    this._boxOpenCount = initial?.boxOpenCount ?? 0;
    this._pity = initial?.pityCounter ?? 0;
    this.rarityPityFallback = initial?.rarityPityCounter ?? 0;
    this.rarityPity = pity
      ? new PityCounter({ interval: pity.interval }, { counter: this.rarityPityFallback })
      : null;
    this.floorRarity = pity?.floorRarity ?? null;
  }

  /** 현재 등급 천장 누적 (저장용) — 미주입이면 초기값 보존. */
  get rarityPityCounter(): number {
    return this.rarityPity?.counter ?? this.rarityPityFallback;
  }

  get boxOpenCount(): number {
    return this._boxOpenCount;
  }

  get pityCounter(): number {
    return this._pity;
  }

  /** 환생 시 골드 상자 가격 누진만 초기화한다. 미발견/pity 진행은 가격 정책과 분리해 보존한다. */
  resetPriceProgress(): void {
    this._boxOpenCount = 0;
  }

  /**
   * 1회 개봉 → 지급할 (id, rarity). 미발견 정의 우선 보장 + 등급 천장(선택) 포함. 풀이 비면 null.
   * 천장: 주입 시 매 open마다 누적 카운터를 advance하고, 보장 pull이면 결과 등급을 floorRarity 이상으로 상향
   * (미발견 보장 위에 layering — 보장 pull은 항상 ≥floor). 단발·10연이 같은 카운터를 공유한다.
   */
  open(discoveredIds: readonly string[]): ModuleDrop | null {
    if (this.pool.length === 0) return null;
    this._boxOpenCount += 1;
    this._pity += 1;
    const guaranteedRarity = this.rarityPity?.advance() ?? false; // 매 pull마다 누적
    const undiscovered = this.pool.filter((d) => !discoveredIds.includes(d.id));
    let drop: ModuleDrop | null;
    if (this._pity >= this.guaranteeAt && undiscovered.length > 0) {
      this._pity = 0;
      drop = this.pickUndiscoveredLowest(undiscovered);
    } else {
      drop = this.pickWeighted();
      if (drop && !discoveredIds.includes(drop.id)) this._pity = 0;
    }
    if (drop && guaranteedRarity) drop = this.applyRarityFloor(drop);
    return drop;
  }

  /**
   * 등급 천장 상향 — drop 등급이 floor 미만이면 풀에서 ≥floor 엔트리로 교체(같은 id 우선, 없으면 ≥floor 균등).
   * ≥floor 엔트리가 풀에 없으면(이론상 도달 X) 원본 유지. rng 1회 추가 소비.
   */
  private applyRarityFloor(drop: ModuleDrop): ModuleDrop {
    if (!this.floorRarity) return drop;
    const floorRank = RARITY_ORDER.indexOf(this.floorRarity);
    if (RARITY_ORDER.indexOf(drop.rarity) >= floorRank) return drop;
    const eligible = this.pool.filter((d) => RARITY_ORDER.indexOf(d.rarity) >= floorRank);
    if (eligible.length === 0) return drop;
    const sameId = eligible.filter((d) => d.id === drop.id);
    const pickFrom = sameId.length > 0 ? sameId : eligible;
    const idx = Math.min(pickFrom.length - 1, Math.floor(this.rng() * pickFrom.length));
    const e = pickFrom[idx]!;
    return { id: e.id, rarity: e.rarity };
  }

  /** 등급 확률 ÷ 그 등급의 엔트리 수 → 등급별 확률 보존 + 같은 등급 내 균등(n분의 1). 단일 rng 호출. */
  private pickWeighted(): ModuleDrop | null {
    const counts = new Map<string, number>();
    for (const d of this.pool) counts.set(d.rarity, (counts.get(d.rarity) ?? 0) + 1);
    const weighted = this.pool.map((d) => ({
      drop: { id: d.id, rarity: d.rarity },
      w: (this.weights[d.rarity] ?? 0) / (counts.get(d.rarity) ?? 1),
    }));
    const total = weighted.reduce((s, e) => s + e.w, 0);
    if (total <= 0) return null;
    let roll = this.rng() * total;
    for (const e of weighted) {
      roll -= e.w;
      if (roll < 0) return e.drop;
    }
    return weighted[weighted.length - 1]?.drop ?? null;
  }

  /** 미발견 보장 — id 단위로 균등 선택 후 그 정의의 **최소 등급**(풀 내 첫 엔트리 = 등급 오름차순)으로 지급. */
  private pickUndiscoveredLowest(entries: readonly RarityDef[]): ModuleDrop {
    const ids = [...new Set(entries.map((e) => e.id))];
    const idx = Math.min(ids.length - 1, Math.floor(this.rng() * ids.length));
    const id = ids[idx] ?? ids[0]!;
    const lowest = this.pool.find((d) => d.id === id)!;
    return { id: lowest.id, rarity: lowest.rarity };
  }

  toState(): GoldModuleBoxState {
    return {
      boxOpenCount: this._boxOpenCount,
      pityCounter: this._pity,
      rarityPityCounter: this.rarityPityCounter,
    };
  }
}
